import { Fragment, type ReactNode } from 'react';
import { ArrowRight, BookText, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ANSWER_PARAGRAPH_CLASS } from '../lib/answerBlocks';
import type { HelpFaqCallout } from '../lib/types';

export interface AnswerCta {
  label: string;
  onClick: () => void;
}

export interface AnswerSource {
  label: string;
  updatedAt?: string;
}

export interface AnswerCardProps {
  /** Already-parsed blocks of `answer_md`. Two to four; four is the ceiling. */
  paragraphs: ReactNode[];
  /** One definition, or none. Never two. */
  callout?: HelpFaqCallout | null;
  cta?: AnswerCta | null;
  /** Scripted answers: renders "From the DVA help guide · updated …" below the card. */
  source?: AnswerSource | null;
  /** Generated answers: renders the dashed "Generated answer" mark plus this line. */
  notice?: string | null;
  /** `fallback` adds the muted "No matching answer" header. */
  variant?: 'answer' | 'fallback';
  /** Fallback extras: chips, the escalation link. */
  children?: ReactNode;
}

/** The assistant's turn: an authored card, not a bubble. Provenance sits outside it. */
export function AnswerCard({
  paragraphs,
  callout = null,
  cta = null,
  source = null,
  notice = null,
  variant = 'answer',
  children = null,
}: AnswerCardProps) {
  return (
    <div className="flex flex-col gap-[7px] animate-[dvaHelpRiseIn_240ms_cubic-bezier(.22,1,.36,1)]">
      <div className="box-border rounded-[10px] border border-[#e5e7eb] bg-white p-[19px]">
        <div className="flex flex-col gap-[13px]">
          {variant === 'fallback' && (
            <span className="flex items-center gap-[7px]">
              <Info className="size-3.5 text-[#717182]" strokeWidth={2} aria-hidden="true" />
              <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-[#717182]">No matching answer</span>
            </span>
          )}

          {paragraphs.map((paragraph, index) =>
            typeof paragraph === 'string' ? (
              <p key={index} className={ANSWER_PARAGRAPH_CLASS}>
                {paragraph}
              </p>
            ) : (
              <Fragment key={index}>{paragraph}</Fragment>
            ),
          )}

          {callout && (
            <div className="flex flex-col gap-1 border-l-2 border-dvaNavy pl-3.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-dvaNavy">{callout.label}</span>
              <span className="text-[13px] leading-[1.6] text-dvaNavy">{callout.body}</span>
            </div>
          )}

          {children}

          {cta && (
            <div className="flex pt-px">
              <Button size="sm" onClick={cta.onClick} className="bg-dvaNavy text-white hover:bg-dvaNavy/90">
                {cta.label}
                <ArrowRight className="size-[15px]" strokeWidth={2.2} aria-hidden="true" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {source && (
        <span className="flex items-center gap-1.5 pl-0.5">
          <BookText className="size-3 text-dvaNavy" strokeWidth={2} aria-hidden="true" />
          <span className="text-[11px] font-medium text-dvaNavy">
            {source.updatedAt ? `${source.label} · updated ${source.updatedAt}` : source.label}
          </span>
        </span>
      )}

      {notice && (
        <span className="flex flex-wrap items-center gap-2 pl-0.5">
          <span className="rounded-[5px] border border-dashed border-[#717182] px-[7px] py-[3px] text-[10px] font-bold uppercase tracking-[0.08em] text-dvaNavy">
            Generated answer
          </span>
          <span className="text-[11px] font-medium text-dvaNavy">{notice}</span>
        </span>
      )}
    </div>
  );
}
